import { apiFetch } from '../../../api/client';
import { parseJsonResponse } from '../../../api/parseResponse';
import type {
  Conversation,
  FollowUp,
  KnowledgeItem,
  SalesAgentBundle,
  SalesLead,
  SalesOverview,
} from '../types/contracts';

async function get<T>(path: string) {
  const res = await apiFetch(path);
  return parseJsonResponse<T>(res);
}

export function fetchAiSalesOverview(from?: string, to?: string) {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const suffix = q.toString() ? `?${q}` : '';
  return get<SalesOverview>(`/platform/ai-sales/overview${suffix}`);
}

export function fetchAiSalesAnalytics(from?: string, to?: string) {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const suffix = q.toString() ? `?${q}` : '';
  return get<SalesOverview>(`/platform/ai-sales/analytics${suffix}`);
}

export function fetchAiSalesConversations() {
  return get<{ items: Conversation[]; connected_channels: string[] }>('/platform/ai-sales/conversations');
}

export function fetchAiSalesConversation(id: string) {
  return get<Conversation>(`/platform/ai-sales/conversations/${encodeURIComponent(id)}`);
}

export function fetchAiSalesLeads(params: Record<string, string | number | undefined> = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') q.set(k, String(v));
  });
  const suffix = q.toString() ? `?${q}` : '';
  return get<{ items: SalesLead[] }>(`/platform/ai-sales/leads${suffix}`);
}

export function fetchAiSalesLead(id: string | number) {
  return get<SalesLead>(`/platform/ai-sales/leads/${id}`);
}

export function fetchAiSalesFollowUps(bucket?: string) {
  const suffix = bucket && bucket !== 'all' ? `?bucket=${encodeURIComponent(bucket)}` : '';
  return get<{ items: FollowUp[] }>(`/platform/ai-sales/follow-ups${suffix}`);
}

export async function patchAiSalesFollowUp(id: string | number, payload: Record<string, unknown>) {
  const res = await apiFetch(`/platform/ai-sales/follow-ups/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<{ id: number; status: string }>(res);
}

export function fetchAiSalesAgent() {
  return get<SalesAgentBundle>('/platform/ai-sales/agent');
}

export async function putAiSalesAgent(payload: Partial<SalesAgentBundle>) {
  const res = await apiFetch('/platform/ai-sales/agent', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<SalesAgentBundle>(res);
}

export function fetchAiSalesKnowledge(category?: string) {
  const suffix = category ? `?category=${encodeURIComponent(category)}` : '';
  return get<{ items: KnowledgeItem[]; pricing_from_subscription: unknown[] }>(
    `/platform/ai-sales/knowledge${suffix}`,
  );
}

export async function postAiSalesKnowledge(payload: Partial<KnowledgeItem>) {
  const res = await apiFetch('/platform/ai-sales/knowledge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<KnowledgeItem>(res);
}

export async function putAiSalesKnowledge(id: string | number, payload: Partial<KnowledgeItem>) {
  const res = await apiFetch(`/platform/ai-sales/knowledge/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<KnowledgeItem>(res);
}

export async function deleteAiSalesKnowledge(id: string | number) {
  const res = await apiFetch(`/platform/ai-sales/knowledge/${id}`, { method: 'DELETE' });
  return parseJsonResponse<null>(res);
}
